import { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Container, Spinner, Alert, ProgressBar } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setOrderInStore } from "../../features/orderSlice";
import { useGetOrderMutation } from "../../features/CallTPApi";
import { IOrder } from "../../interface/IOrder";
import { 
    CheckCircle, 
    ExclamationDiamond, 
    Lock, 
    XCircle, 
    ClockHistory,
    ArrowRepeat,
    ShieldCheck
} from "react-bootstrap-icons";

// Улучшенный компонент для отображения статуса с анимацией
const StatusMessage = ({
    icon: Icon,
    color,
    title,
    description,
    subDescription,
    isLoading = false,
    order,
    error,
    retryCount = 0,
    onRetry
}: {
    icon: React.ElementType;
    color: string;
    title: string;
    description?: string;
    subDescription?: string;
    isLoading?: boolean;
    order: IOrder;
    error?: boolean;
    retryCount?: number;
    onRetry?: () => void;
}) => {

    return (
        <Card.Body className="pt-5 text-center position-relative">
            {/* Прогресс-бар для статусов с загрузкой */}
            {isLoading && (
                <ProgressBar 
                    animated 
                    now={100} 
                    className="mb-4 position-absolute top-0 start-0 w-100 rounded-0" 
                    style={{ height: '4px' }}
                    variant={color.replace('text-', '')}
                />
            )}
            
            {/* Иконка с анимацией */}
            <div className={`mb-3 ${color} position-relative`}>
                <Icon 
                    size={60} 
                    className={isLoading ? 'spinner-pulse' : ''} 
                />
                {retryCount > 0 && !isLoading && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-secondary">
                        {retryCount}
                    </span>
                )}
            </div>

            {/* Основной контент */}
            <h3 className="mb-2">{title}</h3>
            
            {!isLoading && (
                <h4 className="mb-3 text-secondary">Заказ № {order.orderNumber}</h4>
            )}
            
            {description && (
                <p className="fs-5 mb-2">{description}</p>
            )}
            
            {subDescription && (
                <p className="mb-3 text-secondary">{subDescription}</p>
            )}

            {/* Детали заказа (сворачиваемые) */}
            {/* {order && !isLoading && (
                <div className="mb-4">
                    <button 
                        className="btn btn-link text-decoration-none p-0 mb-2"
                        onClick={() => setShowDetails(!showDetails)}
                        type="button"
                    >
                        {showDetails ? '▼ Скрыть детали' : '▶ Показать детали заказа'}
                    </button>
                    
                    {showDetails && (
                        <div className="text-start bg-light p-3 rounded mt-2">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Ticket size={16} className="text-secondary" />
                                <span className="small">Сумма: <strong>{order.summ?.toLocaleString() ?? '0'} ₽</strong></span>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <ClockHistory size={16} className="text-secondary" />
                                <span className="small">Создан: {new Date(order.createDate).toLocaleString()}</span>
                            </div>
                            {order.orderForm === 0 && (
                                <div className="d-flex align-items-center gap-2">
                                    <Envelope size={16} className="text-secondary" />
                                    <span className="small">Билеты будут отправлены на email</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )} */}

            {/* Индикация ошибки */}
            {error && (
                <Alert variant="danger" className="mt-3 mb-4 py-2">
                    <small>Не удалось обновить статус</small>
                </Alert>
            )}

            {/* Кнопка повторной попытки */}
            {error && onRetry && (
                <button 
                    className="btn btn-outline-secondary btn-sm mb-4"
                    onClick={onRetry}
                    type="button"
                >
                    <ArrowRepeat className="me-2" size={14} />
                    Повторить попытку
                </button>
            )}

            {/* Поддержка */}
            <p className="mb-0 small text-secondary">
                Если у вас остались вопросы, обращайтесь в службу поддержки:
                <a 
                    href="mailto:info@ticketspulse.ru" 
                    className="text-dark ms-1 text-decoration-none"
                >
                    info@ticketspulse.ru
                </a>
            </p>
        </Card.Body>
    );
};

// Основной компонент отображения данных заказа
const OrderData = () => {
    type StatusKey = 2 | 3 | 4 | 5 | 6 | 7 | 8;
    
    const dispatch = useDispatch();
    const [getOrder, { isLoading: isMutationLoading }] = useGetOrderMutation();
    const order = useSelector((state: any) => state.order) as IOrder;
    
    const [isPolling, setIsPolling] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [pollingInterval, setPollingInterval] = useState(5000); // Начинаем с 5 секунд
    const [timeInStatus, setTimeInStatus] = useState(0);

    // Инициализация заказа из localStorage
    useEffect(() => {
        const orderString = localStorage.getItem("order");
        if (orderString) {
            try {
                const parsedOrder = JSON.parse(orderString);
                dispatch(setOrderInStore(parsedOrder));
            } catch (error) {
                console.error('Ошибка при парсинге заказа:', error);
                setError('Не удалось загрузить данные заказа');
            }
        }
    }, [dispatch]);

    // Функция обновления статуса с обработкой ошибок
    const updateOrderStatus = useCallback(async (showError = true) => {
        if (!order?.id) return;

        try {
            setError(null);
            const res = await getOrder(order.id).unwrap();
            dispatch(setOrderInStore(res));
            
            // Сброс счетчика ошибок при успехе
            setRetryCount(0);
            
            // Динамическая настройка интервала в зависимости от статуса
            if (res.orderStatus === 4) {
                setPollingInterval(3000); // Чаще проверяем при оплате
            } else if (res.orderStatus === 2) {
                setPollingInterval(5000); // Средний интервал для ожидания
            } else if (res.orderStatus > 4) {
                setIsPolling(false); // Останавливаем опрос при завершении
            }
            
        } catch (err) {
            if (showError) {
                setError('Не удалось обновить статус заказа');
                setRetryCount(prev => prev + 1);
            }
            console.error('Ошибка при обновлении статуса:', err);
        }
    }, [order?.id, dispatch, getOrder]);

    // Таймер нахождения в статусе
    useEffect(() => {
        if (!order?.orderStatus) return;
        
        const timer = setInterval(() => {
            setTimeInStatus(prev => prev + 1);
        }, 1000);
        
        return () => clearInterval(timer);
    }, [order?.orderStatus]);

    // Первичный запрос статуса заказа
    useEffect(() => {
        if (order?.id) {
            updateOrderStatus(true);
        }
    }, [order?.id, updateOrderStatus]);

    // Периодическая проверка статуса с экспоненциальной задержкой при ошибках
    useEffect(() => {
        if (!order?.id || !isPolling) return;

        let currentInterval = pollingInterval;
        if (error) {
            // Экспоненциальная задержка при ошибках (5с, 10с, 20с, 30с max)
            currentInterval = Math.min(30000, 5000 * Math.pow(2, retryCount));
        }

        const interval = setInterval(() => {
            updateOrderStatus(false); // Не показываем ошибку при каждом фоне
        }, currentInterval);

        return () => clearInterval(interval);
    }, [order?.id, isPolling, pollingInterval, error, retryCount, updateOrderStatus]);

    // Автоматический редирект после длительного нахождения в статусе
    useEffect(() => {
        if (timeInStatus > 300 && order?.orderStatus === 4) { // 5 минут в статусе оплаты
            setError('Время ожидания оплаты истекло');
            setIsPolling(false);
        }
    }, [timeInStatus, order?.orderStatus]);

    // Конфигурация статусов с расширенными описаниями
    const statusConfig = useMemo(() => ({
        2: {
            icon: Lock,
            color: "text-primary",
            title: "Заказ зарезервирован",
            description: "Мы сохранили ваш заказ. Пожалуйста, завершите оплату.",
            subDescription: "После оплаты билеты будут отправлены на ваш email",
            isLoading: true,
            action: "Ожидание оплаты"
        },
        3: {
            icon: ClockHistory,
            color: "text-info",
            title: "Обработка платежа",
            description: "Платеж обрабатывается банком",
            subDescription: "Это может занять несколько минут",
            isLoading: true
        },
        4: {
            icon: ArrowRepeat,
            color: "text-secondary",
            title: "Проверка оплаты",
            description: "Мы проверяем данные об оплате...",
            subDescription: `Прошло ${Math.floor(timeInStatus / 60)}:${(timeInStatus % 60).toString().padStart(2, '0')}`,
            isLoading: true
        },
        5: {
            icon: CheckCircle,
            color: "text-success",
            title: "Спасибо за покупку!",
            description: "Ваш заказ успешно оплачен.",
            subDescription: "Билеты отправлены на email, указанный при оформлении.",
            additionalInfo: "Вы также можете найти билеты в личном кабинете"
        },
        6: {
            icon: ExclamationDiamond,
            color: "text-warning",
            title: "Платеж не прошел",
            description: "К сожалению, платеж был отклонен банком",
            subDescription: "Пожалуйста, попробуйте оплатить снова или используйте другую карту"
        },
        7: {
            icon: XCircle,
            color: "text-danger",
            title: "Заказ отменен",
            description: "Время оплаты истекло",
            subDescription: "Вы можете оформить новый заказ"
        },
        8: {
            icon: ShieldCheck,
            color: "text-success",
            title: "Билеты выданы",
            description: "Билеты успешно получены",
            subDescription: "Спасибо за использование нашего сервиса!"
        }
    }), [timeInStatus]);

    if (!order || order.orderStatus === null) {
        return (
            <div className="d-grid align-items-center" style={{ minHeight: '70vh' }}>
                <div className="m-auto" style={{ maxWidth: '768px', width: '100%' }}>
                    <Card className="shadow-sm">
                        <Card.Body className="pt-5 text-center">
                            <Spinner animation="border" variant="secondary" className="mb-3" />
                            <h4>Загрузка данных заказа...</h4>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        );
    }

    const config = statusConfig[order.orderStatus as StatusKey];
    if (!config) return null;

    return (
        <div className="d-grid align-items-center" style={{ minHeight: '70vh' }}>
            <div className="m-auto" style={{ maxWidth: '768px', width: '100%' }}>
                <Card className="shadow-sm border-0">
                    <Card.Header className="text-center bg-transparent border-0 pt-4">
                        <div className="d-flex justify-content-center align-items-center gap-2">
                            <span className="fs-5 fw-normal">Статус вашего заказа</span>
                            {isPolling && (
                                <small className="text-secondary">
                                    <ArrowRepeat className="spin-animation" size={14} />
                                </small>
                            )}
                        </div>
                    </Card.Header>
                    
                    <StatusMessage 
                        {...config} 
                        order={order}
                        error={!!error}
                        retryCount={retryCount}
                        onRetry={() => updateOrderStatus(true)}
                        isLoading={(config as any).isLoading || isMutationLoading}
                    />

                    {/* Дополнительная информация в зависимости от статуса */}
                    {order.orderStatus === 6 && (
                        <Card.Footer className="bg-transparent border-0 pb-4 text-center">
                            <button 
                                className="btn btn-warning px-4"
                                onClick={() => window.location.href = `/paytickets/order?orderId=${order.id}`}
                                type="button"
                            >
                                Повторить оплату
                            </button>
                        </Card.Footer>
                    )}
                </Card>
            </div>
        </div>
    );
};

// Основной компонент страницы
export const PayTicketOrder = () => {
    const dispatch = useDispatch();
    const order = useSelector((state: any) => state.order);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        const orderString = localStorage.getItem("order");
        if (orderString) {
            try {
                const parsedOrder = JSON.parse(orderString);
                dispatch(setOrderInStore(parsedOrder));
            } catch (error) {
                console.error('Ошибка при парсинге заказа:', error);
            }
        }
        setInitialized(true);
    }, [dispatch]);

    if (!initialized) {
        return (
            <Container className="py-5">
                <div className="d-flex justify-content-center">
                    <Spinner animation="border" variant="secondary" />
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            {order ? (
                <OrderData />
            ) : (
                <div className="d-grid align-items-center" style={{ minHeight: '70vh' }}>
                    <div className="m-auto" style={{ maxWidth: '768px', width: '100%' }}>
                        <Card className="shadow-sm border-0">
                            <Card.Header className="text-center bg-transparent border-0 pt-4">
                                <span className="fs-5 fw-normal">Ошибка данных</span>
                            </Card.Header>
                            <Card.Body className="pt-3 pb-5 text-center px-4">
                                <div className="text-danger mb-3">
                                    <XCircle size={60} />
                                </div>
                                <h4 className="mb-3">Заказ не найден</h4>
                                <p className="mb-4 text-secondary">
                                    Возможно, заказ был удален или срок его хранения истек
                                </p>
                                <button 
                                    className="btn btn-dark px-4 mb-4"
                                    onClick={() => window.location.href = '/'}
                                    type="button"
                                >
                                    Вернуться на главную
                                </button>
                                <p className="mb-0 small text-secondary">
                                    Если у вас есть вопросы, обращайтесь в службу поддержки:
                                    <a 
                                        href="mailto:info@ticketspulse.ru" 
                                        className="text-dark ms-1 text-decoration-none"
                                    >
                                        info@ticketspulse.ru
                                    </a>
                                </p>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            )}
        </Container>
    );
};